/**
=========================================================
* Material Dashboard 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material";

// Material Dashboard 2 PRO React TS Base Styles
import colors from "app/assets/theme/base/colors";
import breakpoints from "app/assets/theme/base/breakpoints";
import typography from "app/assets/theme/base/typography";
import boxShadows from "app/assets/theme/base/boxShadows";
import borders from "app/assets/theme/base/borders";
import globals from "app/assets/theme/base/globals";

// Material Dashboard 2 PRO React TS Helper Functions
import boxShadow from "app/assets/theme/functions/boxShadow";
import hexToRgb from "app/assets/theme/functions/hexToRgb";
import linearGradient from "app/assets/theme/functions/linearGradient";
import pxToRem from "app/assets/theme/functions/pxToRem";
import rgba from "app/assets/theme/functions/rgba";

// Material Dashboard 2 PRO React TS components base styles for @mui material components
import sidenav from "app/assets/theme/components/sidenav";
import list from "app/assets/theme/components/list";
import listItem from "app/assets/theme/components/list/listItem";
import listItemText from "app/assets/theme/components/list/listItemText";
import card from "app/assets/theme/components/card";
import cardMedia from "app/assets/theme/components/card/cardMedia";
import cardContent from "app/assets/theme/components/card/cardContent";
import button from "app/assets/theme/components/button";
import iconButton from "app/assets/theme/components/iconButton";
import input from "app/assets/theme/components/form/input";
import inputLabel from "app/assets/theme/components/form/inputLabel";
import inputOutlined from "app/assets/theme/components/form/inputOutlined";
import textField from "app/assets/theme/components/form/textField";
import menu from "app/assets/theme/components/menu";
import menuItem from "app/assets/theme/components/menu/menuItem";
import switchButton from "app/assets/theme/components/form/switchButton";
import divider from "app/assets/theme/components/divider";
import tableContainer from "app/assets/theme/components/table/tableContainer";
import tableHead from "app/assets/theme/components/table/tableHead";
import tableCell from "app/assets/theme/components/table/tableCell";
import linearProgress from "app/assets/theme/components/linearProgress";
import breadcrumbs from "app/assets/theme/components/breadcrumbs";
import slider from "app/assets/theme/components/slider";
import avatar from "app/assets/theme/components/avatar";
import tooltip from "app/assets/theme/components/tooltip";
import appBar from "app/assets/theme/components/appBar";
import tabs from "app/assets/theme/components/tabs";
import tab from "app/assets/theme/components/tabs/tab";
import stepper from "app/assets/theme/components/stepper";
import step from "app/assets/theme/components/stepper/step";
import stepConnector from "app/assets/theme/components/stepper/stepConnector";
import stepLabel from "app/assets/theme/components/stepper/stepLabel";
import stepIcon from "app/assets/theme/components/stepper/stepIcon";
import select from "app/assets/theme/components/form/select";
import formControlLabel from "app/assets/theme/components/form/formControlLabel";
import formLabel from "app/assets/theme/components/form/formLabel";
import checkbox from "app/assets/theme/components/form/checkbox";
import radio from "app/assets/theme/components/form/radio";
import autocomplete from "app/assets/theme/components/form/autocomplete";
import flatpickr from "app/assets/theme/components/flatpickr";
import container from "app/assets/theme/components/container";
import popover from "app/assets/theme/components/popover";
import buttonBase from "app/assets/theme/components/buttonBase";
import icon from "app/assets/theme/components/icon";
import svgIcon from "app/assets/theme/components/svgIcon";
import link from "app/assets/theme/components/link";
import dialog from "app/assets/theme/components/dialog";
import dialogTitle from "app/assets/theme/components/dialog/dialogTitle";
import dialogContent from "app/assets/theme/components/dialog/dialogContent";
import dialogContentText from "app/assets/theme/components/dialog/dialogContentText";
import dialogActions from "app/assets/theme/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
